import {
  Bell,
  ChartNoAxesColumn,
  Building2,
  ChartPie,
  CircleDollarSign,
  ClipboardCheck,
  Cog,
  Download,
  FileText,
  GraduationCap,
  Home,
  Settings,
  ShoppingBag,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * Lightweight shape of the current user as consumed by the sidebar.
 * Mirrors only the fields we read from the Redux store; it is NOT a
 * complete user type.
 */
export type NavigationUser = {
  id: number;
  isStudent?: boolean;
  role?: string;
  meta?: {
    features?: {
      canManageCourses?: boolean;
      canAccessManager?: boolean;
      canAccessDashboards?: boolean;
    };
  };
};

/**
 * Runtime context needed to decide visibility/disabled state of each item.
 * Selectors are computed in the consumer and handed in, keeping the config
 * declarative and easy to unit-test.
 */
export type NavigationContext = {
  user: NavigationUser | undefined;
  hasMockCourses: boolean;
  canAccessMockList: boolean;
  canAccessDownloads: 'allow' | 'deny' | boolean;
  classroomsCount: number;
  companies: Array<{ id: number; name: string }>;
  apiUrl: string;
};

export type NavigationItemKind = 'link' | 'external' | 'widget';

type BaseItem = {
  key: string;
  label: string;
  icon: LucideIcon;
  kind: NavigationItemKind;
  /** When false the item is hidden; when returning "disabled" it renders greyed out. */
  visibility: (ctx: NavigationContext) => 'hidden' | 'visible' | 'disabled';
  /** Tooltip shown when the item is disabled (used by the disabled state). */
  disabledReason?: string;
};

export type LinkItem = BaseItem & {
  kind: 'link';
  to: string;
  exact?: boolean;
};

export type ExternalItem = BaseItem & {
  kind: 'external';
  href: (ctx: NavigationContext) => string;
  target?: '_blank' | '_self';
};

export type WidgetItem = BaseItem & {
  kind: 'widget';
  /** Unique widget id; AppSidebar renders a dedicated component for each id. */
  widget: 'notifications';
};

export type NavigationItem = LinkItem | ExternalItem | WidgetItem;

export type NavigationSection = {
  key: string;
  label: string;
  items: NavigationItem[];
  /** When true, a horizontal separator is rendered above this section. */
  separatorBefore?: boolean;
};

const allowWhen =
  (
    predicate: (ctx: NavigationContext) => boolean,
  ): BaseItem['visibility'] =>
  (ctx) =>
    predicate(ctx) ? 'visible' : 'hidden';

const adminOnly = (predicate: (ctx: NavigationContext) => boolean) =>
  allowWhen((ctx) => !ctx.user?.isStudent && predicate(ctx));

/**
 * Gate for items that monitors/business users may access even though they
 * are not admins — hidden from students regardless of Redux state.
 */
const nonStudentOnly = (predicate: (ctx: NavigationContext) => boolean) =>
  allowWhen((ctx) => !ctx.user?.isStudent && predicate(ctx));

export const NAVIGATION_SECTIONS: NavigationSection[] = [
  {
    key: 'general',
    label: 'Ambiente geral',
    items: [
      {
        key: 'home',
        label: 'Home',
        icon: Home,
        kind: 'link',
        to: '/',
        exact: true,
        visibility: () => 'visible',
      },
      {
        key: 'notifications',
        label: 'Notificações',
        icon: Bell,
        kind: 'link',
        to: '/notifications',
        visibility: () => 'visible',
      },
      {
        key: 'store',
        label: 'Loja',
        icon: ShoppingBag,
        kind: 'link',
        to: '/store',
        exact: true,
        visibility: () => 'visible',
      },
    ],
  },
  {
    key: 'academic',
    label: 'Portal acadêmico',
    separatorBefore: true,
    items: [
      {
        key: 'courses',
        label: 'Cursos',
        icon: GraduationCap,
        kind: 'link',
        to: '/courses',
        visibility: () => 'visible',
      },
      {
        key: 'mocks',
        label: 'Simulados e Questões',
        icon: FileText,
        kind: 'link',
        to: '/mocks',
        exact: true,
        visibility: (ctx) =>
          ctx.canAccessMockList ? 'visible' : 'disabled',
        disabledReason: 'Necessário acesso a algum curso!',
      },
      {
        key: 'my-performance',
        label: 'Desempenho',
        icon: ChartNoAxesColumn,
        kind: 'link',
        to: '/my-performance',
        visibility: (ctx) =>
          ctx.hasMockCourses ? 'visible' : 'disabled',
        disabledReason: 'Necessário acesso a algum curso!',
      },
      {
        key: 'downloads',
        label: 'Downloads',
        icon: Download,
        kind: 'link',
        to: '/downloads',
        visibility: (ctx) =>
          ctx.canAccessDownloads === 'deny' ? 'disabled' : 'visible',
      },
    ],
  },
  {
    key: 'admin',
    label: 'Administração',
    separatorBefore: true,
    items: [
      {
        key: 'manager-legacy',
        label: 'Manager',
        icon: Settings,
        kind: 'external',
        href: (ctx) => `${ctx.apiUrl}/manager`,
        target: '_blank',
        visibility: adminOnly(
          (ctx) => !!ctx.user?.meta?.features?.canManageCourses,
        ),
      },
      {
        key: 'manager-new',
        label: 'Novo Manager',
        icon: Cog,
        kind: 'external',
        href: () => '/manager/v2',
        target: '_blank',
        visibility: adminOnly(
          (ctx) => !!ctx.user?.meta?.features?.canAccessManager,
        ),
      },
      {
        key: 'dashboards',
        label: 'Dashboards',
        icon: ChartPie,
        kind: 'external',
        href: () => '/dashboard/financial',
        visibility: adminOnly(
          (ctx) => !!ctx.user?.meta?.features?.canAccessDashboards,
        ),
      },
      {
        key: 'teacher-mocks',
        label: 'Simulados do professor',
        icon: ClipboardCheck,
        kind: 'link',
        to: '/mocks/teacher',
        visibility: adminOnly(
          (ctx) => ['admin', 'super'].includes(String(ctx.user?.role)),
        ),
      },
      {
        key: 'classrooms',
        label: 'Minhas turmas',
        icon: Users,
        kind: 'link',
        to: '/classrooms',
        visibility: nonStudentOnly((ctx) => ctx.classroomsCount > 0),
      },
    ],
  },
];

/**
 * Dynamic admin items for every company the user belongs to.
 * Returned as a flat list of LinkItem that the consumer appends to the
 * "admin" section when rendering.
 */
export function getCompanyItems(
  _ctx: NavigationContext,
): LinkItem[] {
  // Companies are hidden from the sidebar for now
  return [];
}

export type ResolvedItem =
  | (LinkItem & { state: 'visible' | 'disabled' })
  | (ExternalItem & { state: 'visible' | 'disabled' })
  | (WidgetItem & { state: 'visible' | 'disabled' });

export type ResolvedSection = NavigationSection & {
  items: ResolvedItem[];
};

/** Removes hidden items and hides fully-empty sections. */
export function resolveSections(
  ctx: NavigationContext,
): ResolvedSection[] {
  const withCompanies = NAVIGATION_SECTIONS.map((section) => {
    if (section.key !== 'admin') return section;
    return {
      ...section,
      items: [...section.items, ...getCompanyItems(ctx)],
    };
  });

  return withCompanies
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => {
          const state = item.visibility(ctx);
          return { ...item, state } as NavigationItem & {
            state: 'hidden' | 'visible' | 'disabled';
          };
        })
        .filter((item): item is ResolvedItem => item.state !== 'hidden'),
    }))
    .filter((section) => section.items.length > 0);
}
